import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDown, Check } from 'lucide-react';
import styled from 'styled-components';

import { RootState } from '@store';
import { CountryCode } from '@domains/country';
import { setCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { Country } from '@domains/user/components/Country/Country';

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 5px 7px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
  color: #6b7280;

  &:hover {
    border-color: #e5e7eb;
    background: #f9fafb;
  }

  svg {
    transition: transform 0.2s;
  }

  &[data-open='true'] svg {
    transform: rotate(180deg);
  }

  &[data-single='true'] {
    cursor: default;
    pointer-events: none;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  left: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 200;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-4px);
  transition: opacity 0.15s, transform 0.15s;

  &[data-open='true'] {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  transition: background 0.12s;
  gap: 6px;

  &:hover {
    background: #f3f4f6;
  }

  &[data-active='true'] {
    background: #f5f3ff;
  }
`;

const Wrapper = styled.div`
  position: relative;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export const FlagDropdown = () => {
  const dispatch = useDispatch();
  const { countriesOptions, country } = useSelector((s: RootState) => s.domainUserAuth);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!countriesOptions?.length || !country) return null;

  const hasMultiple = countriesOptions.length > 1;

  return (
    <Wrapper ref={ref}>
      <Trigger
        data-open={open ? 'true' : undefined}
        data-single={!hasMultiple ? 'true' : undefined}
        onClick={() => hasMultiple && setOpen((v) => !v)}
      >
        <Country code={country as CountryCode} />
        {hasMultiple && <ChevronDown size={13} strokeWidth={2} />}
      </Trigger>

      {hasMultiple && (
        <Menu data-open={open ? 'true' : undefined}>
          {countriesOptions.map((opt) => (
            <MenuItem
              key={opt.value}
              data-active={opt.value === country ? 'true' : undefined}
              onClick={() => { dispatch(setCountry(opt.value as CountryCode)); setOpen(false); }}
            >
              <Country code={opt.value as CountryCode} />
              {opt.value === country
                ? <Check size={12} strokeWidth={2.5} color="#5b21b6" />
                : <span style={{ width: 12 }} />
              }
            </MenuItem>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};
